<template>
  <div class="app-saly-ai">
    <!-- Button -->
    <v-btn
      class="sally-ai-btn"
      color="primary"
      fab
      fixed
      @click="toggleDialog">
      <v-img
        src="@/assets/icons/saly-icon.jpg"
        alt="Sally"
        aspect-ratio="1"
        contain />
    </v-btn>

    <!-- Dialog -->
    <v-dialog
      v-model="dialog"
      content-class="sally-ai-dialog"
      transition="slide-y-reverse-transition"
      max-width="400"
      hide-overlay
      persistent>
      <v-card>
        <!-- Header -->
        <v-card-title class="sally-title">
          <transition
            name="content-slide"
            mode="out-in">
            <!-- new task -->
            <div
              v-if="activeTab === NEW_TASK_TAB && !selectedNewTaskOption"
              key="new-task-welcome"
              class="sally-header">
              <span class="sally-header__title">
                Hey there... 🖐️
              </span>
              <span class="sally-header__subtitle">
                My name is Sally, how can I help you?
              </span>
            </div>

            <!-- new query -->
            <div
              v-else-if="activeTab === NEW_TASK_TAB && selectedNewTaskOption"
              key="selected-task"
              class="sally-header">
              <span class="sally-header__title align-self-center">
                {{ selectedNewTaskOption.name }}
              </span>
            </div>

            <!-- query list -->
            <div
              v-else-if="activeTab === MESSAGES_TAB && !selectedMessage"
              key="messages-list"
              class="sally-header">
              <span class="sally-header__title">
                Your Tasks
              </span>
              <span class="sally-header__subtitle">
                Keep track of everything here!
              </span>
            </div>

            <!-- query response -->
            <div
              v-else-if="activeTab === MESSAGES_TAB && selectedMessage"
              key="selected-message"
              class="sally-header">
              <span class="sally-header__query">
                {{ selectedMessage.inputQuery }}
              </span>
            </div>
          </transition>

          <!-- header close button -->
          <span
            class="sourcery__icon-wrapper white pointer top-right"
            @click="dialog = false">
            <v-icon
              color="black"
              size="15">
              mdi-close
            </v-icon>
          </span>
        </v-card-title>

        <!-- Body -->
        <v-card-text class="sally-body">
          <transition
            name="content-slide"
            mode="out-in">
            <!-- new task options -->
            <div
              v-if="activeTab === NEW_TASK_TAB && !selectedNewTaskOption"
              key="new-task-options"
              class="sally-new-task h-450">
              <div
                v-for="option in newTaskOptions"
                :key="option.key"
                class="sally-new-task__option"
                @click="selectNewTaskOption(option)">
                <span class="sally-new-task__option--title">
                  {{ option.name }}
                </span>
                <span class="sally-new-task__option--subtitle">
                  {{ option.subtitle }}
                </span>
              </div>
            </div>

            <!-- new query form -->
            <div
              v-else-if="activeTab === NEW_TASK_TAB && selectedNewTaskOption"
              key="new-task-form"
              class="sally-query-form h-450">
              <div
                v-if="selectedNewTaskOption.key === 'pricing-information'"
                class="d-flex flex-column align-stretch flex-grow-1 gap-5">
                <template v-if="optedInForPricing">
                  <!-- manufacturer combobox -->
                  <v-combobox
                    v-model="manufacturer"
                    :items="manufacturers"
                    class="sally-query-form__combobox"
                    color="black"
                    placeholder="Manufacturer Name"
                    dense
                    flat
                    hide-details
                    outlined
                    solo
                    @change="product = null">
                    <template #append>
                      <span class="sourcery__icon-wrapper black size-16">
                        <v-icon
                          color="white"
                          size="8">
                          $chevronDown
                        </v-icon>
                      </span>
                    </template>
                  </v-combobox>

                  <!-- product combobox -->
                  <v-combobox
                    v-model="product"
                    :disabled="!manufacturer"
                    :items="products"
                    class="sally-query-form__combobox"
                    color="black"
                    placeholder="Product Name"
                    dense
                    flat
                    hide-details
                    outlined
                    solo>
                    <template #append>
                      <span class="sourcery__icon-wrapper black size-16">
                        <v-icon
                          color="white"
                          size="8">
                          $chevronDown
                        </v-icon>
                      </span>
                    </template>
                  </v-combobox>
                </template>

                <template v-else>
                  <span class="sally-query-form__disclaimer">
                    To use the Price Range Finder tool, you need to agree to share workspace
                    pricing data for inclusion in Sourcery's overall pricing AI training
                    dataset. All data will be scrubbed of identifying information (such as
                    project names, users, and workspace identifiers) and generalized to ensure your information remains confidential.
                  </span>
                </template>
              </div>

              <template v-else>
                <v-textarea
                  v-model="inputQuery"
                  :placeholder="selectedNewTaskOption.placeholder"
                  class="sally-query-form__textarea"
                  rows="11"
                  autofocus
                  flat
                  hide-details
                  no-resize
                  solo />
              </template>

              <div class="sally-action-buttons">
                <!-- Opt in for pricing -->
                <v-btn
                  v-if="selectedNewTaskOption.key === 'pricing-information' && !optedInForPricing"
                  color="black"
                  class="white--text"
                  elevation="0"
                  @click="optedInForPricing = true">
                  Opt in for pricing
                </v-btn>

                <!-- Submit button -->
                <v-btn
                  v-else
                  :disabled="!(inputQuery || (manufacturer && product))"
                  color="black"
                  class="white--text"
                  elevation="0"
                  @click="searchQuery">
                  Start the task
                </v-btn>

                <!-- go back button -->
                <v-btn
                  color="black"
                  elevation="0"
                  outlined
                  @click="goToPreviousScreen">
                  Go back
                </v-btn>
              </div>
            </div>

            <!-- task list -->
            <div
              v-else-if="activeTab === MESSAGES_TAB && !selectedMessage"
              key="messages-list"
              class="sally-messages-list h-450">
              <!-- Messages list -->
              <template v-if="messages.length">
                <div
                  v-for="message in messages"
                  :key="message.key"
                  :class="[
                    'sally-messages-list__item',
                    { 'sally-messages-list__item--loading': message.isLoading }
                  ]"
                  @click="message.isLoading || (editingMessage && editingMessage.key === message.key) ? null : openMessage(message)">
                  <!-- loading message -->
                  <template v-if="message.isLoading">
                    <v-icon
                      class="animate-rotate"
                      color="blue"
                      size="16">
                      $rotate
                    </v-icon>

                    <span class="sally-messages-list__item--title blue--text">
                      Working on your task...
                    </span>
                  </template>

                  <!-- editing message -->
                  <template v-else-if="editingMessage && editingMessage.key === message.key">
                    <v-text-field
                      v-model="editingMessage.inputQuery"
                      class="fs-12 font-fam-poppins font-weight-600 lh-normal black--text pa-0 ma-0"
                      autofocus
                      hide-details
                      flat
                      no-resize />

                    <i
                      class="nav-svg-icon icon-send w-20 black--text pointer"
                      @click.stop="saveMessage(editingMessage)" />
                  </template>

                  <!-- message item preview -->
                  <template v-else>
                    <span
                      class="sally-messages-list__item--title">
                      {{ message.inputQuery }}
                    </span>

                    <!-- message delete action -->
                    <div class="d-flex align-center justify-center gap-2">
                      <i
                        class="nav-svg-icon icon-edit w-15 black--text pointer"
                        @click.stop="editMessage(message)" />
                      <v-icon
                        size="16"
                        color="black"
                        @click.stop="deleteMessage(message)">
                        $trash
                      </v-icon>
                    </div>
                  </template>
                </div>
              </template>

              <!-- No messages -->
              <span
                v-else
                class="fs-14 font-fam-poppins font-weight-600 black--text text-center">
                No tasks yet!
              </span>
            </div>

            <!-- task details -->
            <div
              v-else-if="activeTab === MESSAGES_TAB && selectedMessage"
              key="selected-message"
              class="sally-response h-450">
              <!-- Single message details -->
              <!-- eslint-disable -->
              <div
                v-if="selectedMessage.queryResponse"
                class="sally-response__content"
                v-html="formattedAnswer" />

              <!-- loading message -->
              <span
                v-else
                class="fs-12 font-weight-600 lh-normal black--text">
                Loading...
              </span>

              <!-- go back button -->
              <div class="sally-action-buttons">
                <v-btn
                  color="black"
                  elevation="0"
                  outlined
                  @click="goToPreviousScreen">
                  Go back
                </v-btn>
              </div>
            </div>
          </transition>
        </v-card-text>

        <!-- Footer -->
        <v-card-actions class="sally-footer">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            :class="[
              'sally-footer__tab',
              { 'active-tab': activeTab === tab.key }
            ]"
            @click="tab.callback(tab.key)">
            <v-icon
              :color="activeTab === tab.key ? 'white' : 'black'"
              size="25">
              {{ tab.icon }}
            </v-icon>

            <span>
              {{ tab.name }}
            </span>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AirOps from '@airops/airops-js';
import DOMPurify from 'dompurify';
import showdown from 'showdown';
import { mapGetters } from 'vuex';

import { HELP_CENTER } from '@/constants/urls';

import AiropsService from '@/services/airops';

const NEW_TASK_TAB = 'new-task';
const MESSAGES_TAB = 'your-tasks';
const HELP_DESK_TAB = 'help-desk';

export default {
  name: 'AppSallyAI',
  data() {
    return {
      NEW_TASK_TAB,
      MESSAGES_TAB,
      airOps: null,
      activeTab: NEW_TASK_TAB,
      dialog: false,
      editingMessage: null,
      inputQuery: '',
      manufacturer: null,
      manufacturersAndProducts: [],
      messages: [],
      newTaskOptions: [
        {
          name: 'Workspace Questions',
          subtitle: 'I can share tutorials to help you along.',
          key: 'workspace-questions',
          placeholder: 'Type your question here...',
          workflowId: '655ef4b4-96e0-4b77-bca7-5f9888456583',
        },
        {
          name: 'Product Discovery',
          subtitle: 'I can help suggest products for your project.',
          key: 'product-discovery',
          placeholder: 'Type which type of light and application are you looking for? For example: Looking for a downlight for a patient room in a hospital',
          workflowId: '7639ae0a-a540-49af-879e-ec3901ebb616',
        },
        {
          name: 'Ballpark It (Beta)',
          subtitle: 'Get a quick price range based on shared data.',
          key: 'pricing-information',
          placeholder: 'Type your question here...',
          workflowId: '59d30d34-e560-462e-9264-647cc4189c86',
        },
        {
          name: 'Lighting Questions',
          subtitle: 'I can help answer your questions on lighting.',
          key: 'lighting-questions',
          placeholder: 'Type your question here...',
          workflowId: '69900594-d551-473d-93dc-6a5cc711f055',
        },
      ],
      optedInForPricing: false,
      product: null,
      selectedMessage: null,
      selectedNewTaskOption: null,
      tabs: [
        {
          name: 'New Task',
          key: NEW_TASK_TAB,
          icon: '$newTask',
          callback: (key) => {
            if (this.activeTab === key) return;

            this.clearAll();
            this.activeTab = key;
          },
        },
        {
          name: 'Your Tasks',
          key: MESSAGES_TAB,
          icon: '$list',
          callback: (key) => {
            if (this.activeTab === key) return;

            this.clearAll();
            this.activeTab = key;
          },
        },
        {
          name: 'Help Desk',
          key: HELP_DESK_TAB,
          icon: '$account',
          callback: () => {
            window.open(HELP_CENTER, '_blank');
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['userId']),
    /**
     * Get the answer for the selected message
     */
    formattedAnswer() {
      if (!this.selectedMessage) return '';

      const converter = new showdown.Converter({
        openLinksInNewWindow: true,
      });
      const html = converter.makeHtml(this.selectedMessage.queryResponse);
      return DOMPurify.sanitize(html, {
        ADD_ATTR: ['target', 'rel'],
      });
    },
    manufacturers() {
      return this.manufacturersAndProducts.map((m) => m.manufacturer);
    },
    products() {
      return this.manufacturersAndProducts.find((m) => m.manufacturer === this.manufacturer)?.products || [];
    },
  },
  created() {
    this.getProducts();
  },
  mounted() {
    this.airOps = new AirOps();
    this.getMessages();
  },
  methods: {
    /**
     * Clear message and inputQuery
     */
    clearAll() {
      this.clearEditingMessage();
      this.clearMessage();
      this.clearQuery();
      this.clearManufacturerAndProduct();
    },

    /**
     * Clear editing message
     */
    clearEditingMessage() {
      this.editingMessage = null;
    },

    /**
     * Clear manufacturer
     */
    clearManufacturerAndProduct() {
      this.manufacturer = null;
      this.product = null;
    },

    /**
     * Clear message
     */
    clearMessage() {
      this.selectedMessage = null;
    },

    /**
     * Clear inputQuery
     */
    clearQuery() {
      this.selectedNewTaskOption = null;
      this.inputQuery = '';
    },

    /**
     * Delete message
     */
    async deleteMessage(message) {
      await AiropsService.deleteAiropsMessage({
        executionId: message.key,
        timestamp: message.timestamp,
        userId: this.userId,
      });

      this.messages = this.messages.filter((m) => m.key !== message.key);
    },

    /**
     * Edit message
     */
    editMessage(message) {
      this.editingMessage = {
        ...message,
      };
    },

    /**
     * Fetch response from airops and save to DB
     */
    async fetchResponseAndSave(message) {
      const results = await this.airOps.apps.getResults({
        executionId: message.key,
      });

      await AiropsService.updateAiropsMessage({
        executionId: message.key,
        queryResponse: results.output,
        timestamp: message.timestamp,
        userId: this.userId,
      });

      this.messages.map((m) => {
        if (m.key === message.key) {
          m.isLoading = false;
          m.queryResponse = results.output;
        }
      });
    },

    /**
     * Get messages from DB
     */
    async getMessages() {
      const { data } = await AiropsService.getAiropsMessage({
        userId: this.userId,
      });

      const messages = data?.response?.data || [];

      this.messages = messages.map((m) => ({
        isLoading: false,
        ...m,
        key: m.executionId,
      }));
    },

    /**
     * Get products from DB
     */
    async getProducts() {
      const { data } = await AiropsService.fetchAirOpsProducts();
      const products = data?.response?.products || '[]';
      const parsedProducts = JSON.parse(products);

      this.manufacturersAndProducts = parsedProducts;
    },

    /**
     * Go to previous screen
     */
    goToPreviousScreen() {
      if (this.activeTab === NEW_TASK_TAB) {
        this.clearQuery();
        this.clearManufacturerAndProduct();
      } else if (this.activeTab === MESSAGES_TAB) {
        this.clearMessage();
      }
    },

    /**
     * Open message & get response from AirOps
     */
    async openMessage(message) {
      this.selectedMessage = message;

      if (message.queryResponse && message.queryResponse !== '') return;

      await this.fetchResponseAndSave(message);
    },

    /**
     * Save execution id to DB
     */
    async saveExecutionId(message, executionId) {
      const { userId } = this;

      await AiropsService.saveAiropsMessage({
        executionId,
        inputQuery: message.inputQuery,
        queryType: message.queryType,
        timestamp: message.timestamp,
        userId,
      });

      this.messages.unshift(message);
    },

    /**
     * Rename input query of message
     */
    async saveMessage(message) {
      await AiropsService.renameAiropsMessage({
        executionId: message.key,
        inputQuery: message.inputQuery,
        timestamp: message.timestamp,
        userId: this.userId,
      });

      this.messages = this.messages.map((m) => (m.key === message.key ? message : m));

      this.clearEditingMessage();
    },

    /**
     * Search query by manufacturer and product OR input query
     */
    async searchQuery() {
      const { inputQuery, manufacturer, product, selectedNewTaskOption, messages } = this;
      const { key, workflowId } = selectedNewTaskOption;

      if (!(inputQuery || (manufacturer && product))) return;

      const timestamp = new Date().toISOString();
      let query = inputQuery;

      let payload = {
        inputs: {
          query: inputQuery,
        },
      };

      if (key === 'pricing-information') {
        query = `Pricing information for ${manufacturer} ${product}`;

        payload = {
          inputs: {
            manufacture: manufacturer,
            product,
          },
        };
      }

      // Execute an workflow
      const response = await this.airOps.apps.execute({
        appId: workflowId,
        payload,
        stream: true,
        streamCallback: () => {
          // do nothing
        },
        streamCompletedCallback: () => {
          const message = messages.find((m) => m.key === response.executionId);
          this.fetchResponseAndSave(message);
        },
      });

      const message = {
        inputQuery: query,
        timestamp,
        key: response.executionId,
        queryType: selectedNewTaskOption.key,
        isLoading: true,
        queryResponse: '',
      };

      this.saveExecutionId(message, response.executionId);

      this.activeTab = MESSAGES_TAB;
      this.clearQuery();
      this.clearManufacturerAndProduct();
    },

    /**
     * Select new task option
     */
    selectNewTaskOption(option) {
      this.selectedNewTaskOption = option;
    },

    /**
     * Toggle dialog
     */
    toggleDialog() {
      this.dialog = !this.dialog;
      if (!this.dialog) {
        this.goToPreviousScreen();
      }
    },
  },
};
</script>

<style scoped lang="scss">
$linear-gradient: linear-gradient(135deg, #2479FF 0%, #1D61CD 49%, #164999 100%);

.sally-ai-btn {
  position: fixed;
  bottom: calc(50% - 50px);
  height: 45px !important;
  right: 12px;
  width: 45px !important;
  overflow: hidden;

  @media (min-width: 992px) {
    bottom: 20px;
    height: 65px !important;
    right: 20px;
    width: 65px !important;
  }
}

::v-deep .v-dialog.sally-ai-dialog {
  position: fixed;
  bottom: 80px;
  right: 40px;
  width: calc(100vw - 100px);

  box-shadow: 0 0 25px 0 rgba(var(--v-blackRGB), 0.15);

  @media (min-width: 992px) {
    right: 0;
  }

  .h-450 {
    max-height: calc(100vh - 425px);
    height: 450px;
    overflow-y: auto;
  }

  .w-15 {
    width: 15px;
  }

  .w-20 {
    width: 20px;
  }

  .size-16 {
    height: 16px;
    width: 16px;
  }

  .sally-title {
    flex-wrap: nowrap;
    color: var(--v-white-base);
    background: $linear-gradient;
    border-bottom: none;
    padding: 25px 15px;

    @media (min-width: 992px) {
      padding: 25px;
    }

    .sourcery__icon-wrapper {
      border-radius: 5px;
      flex-shrink: 0;
    }

    .top-right {
      position: absolute;
      top: 15px;
      right: 15px;
    }
  }

  .sally-header {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    align-items: flex-start;
    justify-content: center;
    gap: 5px;
    color: var(--v-white-base);

    &__title {
      font-family: 'Poppins', sans-serif;
      font-size: 22px;
      font-weight: 600;
      line-height: normal;
    }

    &__subtitle {
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      font-weight: 600;
      line-height: normal;
    }

    &__query {
      font-family: 'Poppins', sans-serif;
      font-size: 18px;
      font-weight: 600;
      line-height: normal;
      letter-spacing: 0;

      display: -webkit-box;
      overflow: hidden;
      word-break: keep-all;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }

  .sally-body {
    padding: 0;

    .sally-new-task {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 20px;
      padding: 30px 15px;

      @media (min-width: 992px) {
        padding: 30px;
      }

      &__option {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 2px;
        padding: 20px 15px;
        border-radius: 10px;
        box-shadow: inset 0 0 0 1px var(--v-black-base);
        cursor: pointer;

        @media (min-width: 992px) {
          padding: 20px;
        }

        &--title {
          color: var(--v-black-base);
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          font-weight: 600;
          line-height: 24px;
        }

        &--subtitle {
          color: var(--v-black-base);
          font-size: 12px;
          font-weight: 600;
          line-height: 16px;
        }
      }
    }

    .sally-query-form {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 20px;
      padding: 30px 15px;

      @media (min-width: 992px) {
        padding: 30px 25px;
      }

      &__combobox {
        color: var(--v-black-base);
        flex-grow: 0;
        border-radius: 5px;
        border: 1px solid var(--v-black-base);

        .v-input__slot {
          padding: 0;

          .v-select__slot {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 20px 15px 20px 20px;

            input {
              padding: 0;
              font-family: 'Poppins', sans-serif;
              font-size: 12px;
              font-weight: 600;
              letter-spacing: 0;
              line-height: normal;

              &::placeholder {
                color: var(--v-darkGrey-base);
              }
            }
          }
        }
      }

      &__disclaimer {
        color: var(--v-black-base);
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        letter-spacing: 0;
      }

      &__textarea {
        color: var(--v-black-base);
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;

        .v-input__slot {
          padding: 0;
        }

        textarea {
          padding: 0 !important;
        }

        ::placeholder {
          color: var(--v-darkGrey-base);
        }
      }
    }

    .sally-action-buttons {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 10px;

      .v-btn > .v-btn__content {
        font-size: 12px;
        font-weight: 700;
        line-height: 16px;
        letter-spacing: 0;
      }
    }

    .sally-messages-list {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: stretch;
      gap: 20px;
      padding: 30px 15px;

      @media (min-width: 992px) {
        padding: 30px;
      }

      &__item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 15px;
        padding: 20px 15px;
        border-radius: 5px;
        box-shadow: inset 0 0 0 1px var(--v-black-base);
        cursor: pointer;

        @media (min-width: 992px) {
          padding: 20px;
        }

        &--loading {
          box-shadow: inset 0 0 0 1px rgba(var(--v-blackRGB), 0.25);
          cursor: default;
        }

        &--title {
          flex-grow: 1;
          color: var(--v-black-base);
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0;
          line-height: normal;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .sally-response {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 10px;
      padding: 30px 15px;

      @media (min-width: 992px) {
        padding: 30px 25px;
      }

      &__content {
        flex-grow: 1;

        div,
        h1, h2, h3, h4, h5, h6,
        p, span,
        ul,
        li {
          font-size: 14px;
          font-weight: 600;
          line-height: 20px;
          letter-spacing: 0;
          color: var(--v-black-base);
        }
      }
    }
  }

  .sally-footer {
    justify-content: space-between;
    padding: 20px 15px;
    background: var(--v-lightGrey-base);
    border-top: none;

    @media (min-width: 992px) {
      padding: 20px 30px;
    }

    &__tab {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 6px;
      padding: 10px 12px;
      border-radius: 10px;
      background: var(--v-white-base);
      color: var(--v-black-base);
      cursor: pointer;
      font-size: 11px;
      font-weight: 800;
      line-height: 11px;

      &.active-tab {
        background: $linear-gradient;
        color: var(--v-white-base);
      }
    }
  }

  .animate-rotate {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  // Content slide transitions - matching header timing
  .content-slide-enter-active,
  .content-slide-leave-active {
    transition: opacity 150ms ease;
  }

  .content-slide-enter,
  .content-slide-leave-to {
    opacity: 0;
  }
}
</style>